import { Box, Typography } from "@outschool/backpack";
import { REFER_CREDIT_CENTS } from "@outschool/business-rules";
import { SignupModalQuery } from "@outschool/gql-frontend-generated";
import {
  renderCurrencyString,
  Trans,
  useTranslation,
} from "@outschool/localization";
import {
  useConfetti,
  UserHeadshotImage,
} from "@outschool/ui-components-shared";
import React, { useEffect } from "react";

export const ReferrerHeader = ({
  referrer,
}: {
  referrer: NonNullable<SignupModalQuery["referrer"]>;
}) => {
  const { t } = useTranslation("client\\components\\sharing\\ReferrerHeader");
  const isOutreachReferrer = !!referrer.leader?.approved;
  const referrerProfile = isOutreachReferrer
    ? referrer.leader
    : referrer.parent;

  const name = isOutreachReferrer
    ? referrer.leader?.name
    : referrer.parent?.publicName;

  const canShowSocialData = !referrerProfile?.socialOptOut;

  const { confettiCannon, fireConfetti } = useConfetti(confettiOptions);
  useEffect(() => {
    setTimeout(fireConfetti, 750);
  }, [fireConfetti]);

  const creditString = isOutreachReferrer
    ? t(
        "up to $50 off your first class or $20 off your first month of membership",
      )
    : t("{{referCredits}} Outschool credit", {
        referCredits: renderCurrencyString({
          priceInCents: REFER_CREDIT_CENTS,
          showCurrencyCode: false,
        }),
      });

  return (
    <Box
      data-test-id="signup-modal-referral-header"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      {confettiCannon}
      {referrerProfile?.photo && canShowSocialData && (
        <UserHeadshotImage
          data-test-id="referrer-avatar"
          user={referrerProfile}
        />
      )}
      <Box
        data-test-id="referrer-message"
        sx={{
          marginBottom: "0.5rem",
          marginTop: "1rem",
          textAlign: "center",
          fontWeight: "fontWeightBold",
        }}
      >
        {!canShowSocialData ? (
          <Trans t={t}>You got {{ creditString }} from your friend</Trans>
        ) : (
          <Trans t={t}>
            You got {{ creditString }} from {{ referrerName: name?.trim() }}
          </Trans>
        )}
      </Box>
      <Typography
        variant="body2"
        sx={{ color: "grey.600", marginBottom: "2rem" }}
      >
        {isOutreachReferrer
          ? t("Sign up to claim your discount!")
          : t("Sign up to claim your credits!")}
      </Typography>
    </Box>
  );
};

const confettiOptions = {
  angle: 90,
  spread: 180,
  startVelocity: 25,
  elementCount: 50,
};
